// src/components/resources/calculators/UgcCostCalculator.tsx

'use client'

import { useCallback, useMemo, useState, type KeyboardEvent } from 'react'
import Link from 'next/link'
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline'
import { cn } from '~/lib/utils'
import { downloadCsv } from '~/lib/utils/csv'
import { debounce } from '~/lib/utils/events'
import { createEngagementTimers, sendResourceEvent } from '~/lib/gtag'
import { FAQ } from '~/components/shared/FAQ'
import { RELATED_GLOSSARY, RELATED_TOOLS } from './UgcCostCalculator.related'
import {
  AGENCY_TYPICAL_RETAINER_HIGH,
  CREATOR_TIERS,
  HEALTHY_MONTHLY_CONCEPTS_MAX,
  HEALTHY_MONTHLY_CONCEPTS_MIN,
  NICHES,
  PRODUCTION_MODES,
  RATE_SOURCES,
  RIGHTS_TERMS,
  calculateUgcCost,
  clampCreatives,
  clampWinRate,
  formatUsdRange,
  type CreatorTier,
  type Niche,
  type ProductionMode,
  type RightsTerm,
  type UgcCostInputs,
} from './UgcCostCalculator.logic'

/**
 * Standalone-render FAQ fallback. The calculator route passes `faqItems`
 * from the registry record so the visible FAQ matches FAQPage JSON-LD.
 */
const FAQ_ITEMS = [
  {
    question: 'How much does a UGC video cost in 2026?',
    answer:
      'Marketplace data puts the average single UGC deliverable around $190–$212 (Collabstr ~$190; Influee / Social Native ~$198–$212). Beginners typically charge $75–$200, mid-tier creators $300–$800 for reliable ad-ready footage, and specialists $600–$1,500+. Those figures are organic-only base rates — paid ad usage rights usually add 25–150% on top.',
  },
  {
    question: 'Do UGC usage rights cost extra?',
    answer:
      'Almost always. The base fee usually covers organic posting on the brand feed. Six-month paid rights commonly add 25–100% of base, twelve-month rights 50–150%, and perpetual/unlimited rights 100–150%+. Whitelisting (running ads from the creator’s handle) and category exclusivity are separate line items. Always confirm what a quote includes before comparing prices.',
  },
  {
    question: 'What is cost-per-winner, and why does it matter more than cost-per-video?',
    answer:
      'Most individual UGC videos do not become scalable winners. In this calculator, cost-per-winner = monthly production spend ÷ expected winning finished videos (volume × win rate). Buying three $500 videos that all miss leaves an infinite cost-per-winner; spending $300 on AI discovery and rebuilding only the two finished videos that clear hook-rate and CPA filters usually produces a lower cost-per-winner at the same or lower total budget.',
  },
  {
    question: 'When should I use AI UGC vs real creators?',
    answer:
      'Use AI as a testing layer for volume discovery ($5–$25 per finished variant, or $19–$399/mo plans, typically with no usage-rights fees). Rebuild validated winners with real mid-tier or specialist creators before you put meaningful spend behind a face in trust-led categories (supplements, finance, beauty). The hybrid path in this calculator models that exact sequence.',
  },
  {
    question: 'How many UGC creatives should I produce per month?',
    answer:
      'Brands holding CPA below the 2026 Meta all-industry median (~$38) typically keep 6–10 distinct creative concepts in active rotation, producing three to four new concepts monthly with two to three hook variants each. Below six finished videos/month on this slider, the cheapest win is usually more volume — not cheaper videos. Pair this calculator with the Creative Testing Calculator to size the media budget behind each concept.',
  },
  {
    question: 'Is this UGC cost calculator free?',
    answer:
      'Yes. It runs entirely in your browser, requires no sign-up, cites every rate band to published 2026 sources, and exports a CSV of your scenario. Estimates are planning ranges, not quotes.',
  },
]

const CREATOR_TIER_OPTIONS = Object.keys(CREATOR_TIERS) as CreatorTier[]
const PRODUCTION_MODE_OPTIONS = Object.keys(PRODUCTION_MODES) as ProductionMode[]

/**
 * Props for the UGC cost calculator. The calculator route passes `faqItems`
 * straight from the registry record's `faqs` — the same array
 * `WithResourceInstanceStructuredData` emits as `FAQPage` JSON-LD — so the
 * visible FAQ and the structured data share a single source.
 *
 * @interface UgcCostCalculatorProps
 * @property {{ question: string; answer: string }[]} [faqItems] - FAQ entries from the registry.
 */
export interface UgcCostCalculatorProps {
  /** FAQ entries to render. Passed from the registry record so visible
   *  copy matches the JSON-LD. */
  faqItems?: { question: string; answer: string }[]
}

/**
 * Interactive UGC cost calculator with DIY, agency, and hybrid AI paths.
 *
 * @param {UgcCostCalculatorProps} [props] - Optional FAQ override from the route.
 * @param {{ question: string; answer: string }[]} [props.faqItems] - Registry FAQs for JSON-LD parity.
 * @returns {JSX.Element} The rendered calculator widget.
 */
export function UgcCostCalculator({
  faqItems = FAQ_ITEMS.map((item) => ({ ...item })),
}: UgcCostCalculatorProps = {}): JSX.Element {
  const [creativesPerMonth, setCreativesPerMonth] = useState(8)
  const [creatorTier, setCreatorTier] = useState<CreatorTier>('mid')
  const [rightsTerm, setRightsTerm] = useState<RightsTerm>('paid_6mo')
  const [includeWhitelisting, setIncludeWhitelisting] = useState(false)
  const [rawFootage, setRawFootage] = useState(false)
  const [exclusivity, setExclusivity] = useState(false)
  const [niche, setNiche] = useState<Niche>('general')
  const [productionMode, setProductionMode] = useState<ProductionMode>('diy_creators')
  const [expectedWinRatePct, setExpectedWinRatePct] = useState(15)
  const [includeInternalOverhead, setIncludeInternalOverhead] = useState(false)

  const isAgency = productionMode === 'agency'
  const isHybrid = productionMode === 'hybrid_ai'
  // Agency retainers are modeled all-in; DIY add-ons don't change the band.
  const diyAddOnsDisabled = isAgency
  // Hybrid rebuilds always price mid-tier creators; DIY producer-hours don't apply.
  const creatorTierDisabled = isAgency || isHybrid
  const internalOverheadDisabled = isAgency || isHybrid

  const inputs: UgcCostInputs = useMemo(
    () => ({
      creativesPerMonth,
      creatorTier,
      rightsTerm,
      includeWhitelisting,
      rawFootage,
      exclusivity,
      niche,
      productionMode,
      expectedWinRatePct,
      includeInternalOverhead,
    }),
    [
      creativesPerMonth,
      creatorTier,
      rightsTerm,
      includeWhitelisting,
      rawFootage,
      exclusivity,
      niche,
      productionMode,
      expectedWinRatePct,
      includeInternalOverhead,
    ],
  )

  const results = useMemo(() => calculateUgcCost(inputs), [inputs])

  const [getTotalMs, getDeltaMs] = useMemo(() => createEngagementTimers(), [])

  const _debouncedInteract = useMemo(
    () =>
      debounce((...args: unknown[]) => {
        const interactionType = (args[0] as string) ?? 'input_change'
        sendResourceEvent('interact', {
          resourceType: 'calculator',
          resourceSlug: 'ugc-cost-calculator',
          engagementMs: getDeltaMs(),
          params: { interaction_type: interactionType },
        })
      }, 400),
    [getDeltaMs],
  )

  const trackInteract = useCallback(
    (interactionType: string): void => {
      _debouncedInteract(interactionType)
    },
    [_debouncedInteract],
  )

  const reset = useCallback(() => {
    setCreativesPerMonth(8)
    setCreatorTier('mid')
    setRightsTerm('paid_6mo')
    setIncludeWhitelisting(false)
    setRawFootage(false)
    setExclusivity(false)
    setNiche('general')
    setProductionMode('diy_creators')
    setExpectedWinRatePct(15)
    setIncludeInternalOverhead(false)
    trackInteract('reset')
  }, [trackInteract])

  const exportCsv = useCallback(() => {
    // Export mode-effective values so the CSV can reproduce the selected total
    // (hybrid always rebuilds mid-tier; agency ignores DIY add-ons).
    const effectiveTier = isHybrid || isAgency ? 'mid (mode default)' : CREATOR_TIERS[creatorTier].label
    const effectiveRights = isAgency ? 'n/a (included in retainer)' : RIGHTS_TERMS[rightsTerm].label
    const effectiveNiche = isAgency ? 'n/a (absorbed in retainer)' : NICHES[niche].label
    const effectiveWhitelist = isAgency
      ? 'n/a (included in retainer)'
      : includeWhitelisting
        ? 'yes'
        : 'no'
    const effectiveRaw = isAgency ? 'n/a (included in retainer)' : rawFootage ? 'yes' : 'no'
    const effectiveExclusivity = isAgency
      ? 'n/a (included in retainer)'
      : exclusivity
        ? 'yes'
        : 'no'
    const effectiveOverhead =
      isAgency || isHybrid
        ? 'n/a (DIY-only)'
        : includeInternalOverhead
          ? 'yes'
          : 'no'

    const rows: string[][] = [
      ['Field', 'Value'],
      ['Creatives per month', String(creativesPerMonth)],
      ['Creator tier (effective)', effectiveTier],
      ['Rights term (effective)', effectiveRights],
      ['Niche (effective)', effectiveNiche],
      ['Production mode', PRODUCTION_MODES[productionMode].label],
      ['Whitelisting (effective)', effectiveWhitelist],
      ['Raw footage (effective)', effectiveRaw],
      ['Exclusivity (effective)', effectiveExclusivity],
      ['Expected win rate %', String(expectedWinRatePct)],
      ['Internal overhead (effective)', effectiveOverhead],
      [
        'Bundle discount % (DIY only)',
        productionMode === 'diy_creators' ? String(results.bundleDiscountPct) : 'n/a',
      ],
      ['DIY external / month', formatUsdRange(results.diyExternalMonthly)],
      ['DIY all-in / month', formatUsdRange(results.diyAllInMonthly)],
      ['Agency / month', formatUsdRange(results.agencyMonthly)],
      ['Hybrid AI→creator / month', formatUsdRange(results.hybridMonthly)],
      ['Selected / month', formatUsdRange(results.selectedMonthly)],
      ['Cost per creative', formatUsdRange(results.costPerCreative)],
      ['Expected winning videos', results.expectedWinners.toFixed(2)],
      ['Hybrid rebuild count', results.hybridRebuildCount.toFixed(2)],
      ['Cost per winning video', formatUsdRange(results.costPerWinner)],
      ['Stack mode', results.stackMode],
      ['Sources', RATE_SOURCES.map((s) => s.label).join('; ')],
    ]
    downloadCsv('adsights-ugc-cost-estimate', rows)
    sendResourceEvent('complete', {
      resourceType: 'calculator',
      resourceSlug: 'ugc-cost-calculator',
      engagementMs: getTotalMs(),
      params: {
        outcome: 'export_csv',
        creatives: creativesPerMonth,
        mode: productionMode,
        rights: rightsTerm,
      },
    })
  }, [
    creativesPerMonth,
    creatorTier,
    rightsTerm,
    niche,
    productionMode,
    includeWhitelisting,
    rawFootage,
    exclusivity,
    expectedWinRatePct,
    includeInternalOverhead,
    isAgency,
    isHybrid,
    results,
    getTotalMs,
  ])

  return (
    <div className="space-y-10">
      <div className="grid gap-8 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
        <section className="space-y-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-900">
          <header className="space-y-1">
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
              Estimate your monthly UGC budget
            </h2>
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Independently researched 2026 rate bands with rights, whitelisting, niche premiums,
              and cost-per-winner — not just a base-rate × volume stub. Methodology citations live
              in the guide below this widget.
            </p>
          </header>

          <label className="block space-y-2">
            <span className="text-sm font-medium text-slate-800 dark:text-slate-100">
              Finished videos needed per month
            </span>
            <input
              type="range"
              min={1}
              max={60}
              value={creativesPerMonth}
              onChange={(e) => {
                setCreativesPerMonth(clampCreatives(Number(e.target.value)))
                trackInteract('creatives_change')
              }}
              className="w-full accent-teal-600"
            />
            <div className="flex items-center justify-between text-sm text-slate-600 dark:text-slate-300">
              <span>{creativesPerMonth} finished videos</span>
              <span>
                Typical slate: {HEALTHY_MONTHLY_CONCEPTS_MIN}–{HEALTHY_MONTHLY_CONCEPTS_MAX}
              </span>
            </div>
            <p className="text-xs text-slate-500">
              Counts finished deliverables (a concept may ship multiple hook variants). Distinct
              concept diversity is a separate planning choice — see the FAQ.
            </p>
          </label>

          <fieldset className="space-y-2" disabled={creatorTierDisabled}>
            <legend className="text-sm font-medium text-slate-800 dark:text-slate-100">
              Creator tier (organic base)
            </legend>
            <div
              className="grid gap-2 sm:grid-cols-3"
              role="radiogroup"
              aria-label="Creator tier"
              onKeyDown={(e) =>
                handleRadioGroupKeyDown(e, CREATOR_TIER_OPTIONS, creatorTier, (next) => {
                  setCreatorTier(next)
                  trackInteract('tier_change')
                })
              }
            >
              {CREATOR_TIER_OPTIONS.map((tier) => (
                <button
                  key={tier}
                  type="button"
                  role="radio"
                  aria-checked={creatorTier === tier}
                  tabIndex={creatorTierDisabled ? -1 : creatorTier === tier ? 0 : -1}
                  disabled={creatorTierDisabled}
                  onClick={() => {
                    setCreatorTier(tier)
                    trackInteract('tier_change')
                  }}
                  className={cn(
                    'rounded-xl border px-3 py-3 text-left text-sm transition',
                    creatorTier === tier
                      ? 'border-teal-600 bg-teal-50 text-teal-900 dark:border-teal-400 dark:bg-teal-950/40 dark:text-teal-100'
                      : 'border-slate-200 hover:border-slate-300 dark:border-slate-700',
                    creatorTierDisabled && 'cursor-not-allowed opacity-60',
                  )}
                >
                  <div className="font-medium">{CREATOR_TIERS[tier].label}</div>
                  <div className="mt-1 text-xs opacity-80">
                    {formatUsdRange(CREATOR_TIERS[tier].base)} base
                  </div>
                </button>
              ))}
            </div>
            {isAgency && (
              <p className="text-xs text-slate-500">
                Agency packages are modeled as all-in retainers — creator tier does not change the
                band.
              </p>
            )}
            {isHybrid && (
              <p className="text-xs text-slate-500">
                Hybrid rebuilds always use mid-tier creator rates for validated winners.
              </p>
            )}
          </fieldset>

          <label className="block space-y-2">
            <span className="text-sm font-medium text-slate-800 dark:text-slate-100">
              Usage rights duration
            </span>
            <select
              value={rightsTerm}
              disabled={diyAddOnsDisabled}
              onChange={(e) => {
                setRightsTerm(e.target.value as RightsTerm)
                trackInteract('rights_change')
              }}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950"
            >
              {(Object.keys(RIGHTS_TERMS) as RightsTerm[]).map((term) => (
                <option key={term} value={term}>
                  {RIGHTS_TERMS[term].label}
                </option>
              ))}
            </select>
            <p className="text-xs text-slate-500">
              {diyAddOnsDisabled
                ? 'Agency retainers typically include usage rights inside the package price.'
                : RIGHTS_TERMS[rightsTerm].description}
            </p>
          </label>

          <label className="block space-y-2">
            <span className="text-sm font-medium text-slate-800 dark:text-slate-100">Niche</span>
            <select
              value={niche}
              disabled={diyAddOnsDisabled}
              onChange={(e) => {
                setNiche(e.target.value as Niche)
                trackInteract('niche_change')
              }}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950"
            >
              {(Object.keys(NICHES) as Niche[]).map((n) => (
                <option key={n} value={n}>
                  {NICHES[n].label}
                  {NICHES[n].multiplier !== 1 ? ` (×${NICHES[n].multiplier})` : ''}
                </option>
              ))}
            </select>
            {diyAddOnsDisabled && (
              <p className="text-xs text-slate-500">
                Niche premiums are absorbed into the agency per-deliverable / retainer model.
              </p>
            )}
          </label>

          <fieldset className="space-y-2">
            <legend className="text-sm font-medium text-slate-800 dark:text-slate-100">
              Production path
            </legend>
            <div
              className="grid gap-2"
              role="radiogroup"
              aria-label="Production path"
              onKeyDown={(e) =>
                handleRadioGroupKeyDown(e, PRODUCTION_MODE_OPTIONS, productionMode, (next) => {
                  setProductionMode(next)
                  trackInteract('mode_change')
                })
              }
            >
              {PRODUCTION_MODE_OPTIONS.map((mode) => (
                <button
                  key={mode}
                  type="button"
                  role="radio"
                  aria-checked={productionMode === mode}
                  tabIndex={productionMode === mode ? 0 : -1}
                  onClick={() => {
                    setProductionMode(mode)
                    trackInteract('mode_change')
                  }}
                  className={cn(
                    'rounded-xl border px-3 py-3 text-left text-sm transition',
                    productionMode === mode
                      ? 'border-teal-600 bg-teal-50 dark:border-teal-400 dark:bg-teal-950/40'
                      : 'border-slate-200 dark:border-slate-700',
                  )}
                >
                  <div className="font-medium">{PRODUCTION_MODES[mode].label}</div>
                  <div className="mt-0.5 text-xs text-slate-600 dark:text-slate-300">
                    {PRODUCTION_MODES[mode].description}
                  </div>
                </button>
              ))}
            </div>
          </fieldset>

          <div className="grid gap-3 sm:grid-cols-2">
            <Toggle
              label="Whitelisting / Spark Ads"
              checked={includeWhitelisting}
              disabled={diyAddOnsDisabled}
              onChange={(next) => {
                setIncludeWhitelisting(next)
                trackInteract('whitelist_toggle')
              }}
            />
            <Toggle
              label="Raw footage delivery"
              checked={rawFootage}
              disabled={diyAddOnsDisabled}
              onChange={(next) => {
                setRawFootage(next)
                trackInteract('raw_toggle')
              }}
            />
            <Toggle
              label="Category exclusivity"
              checked={exclusivity}
              disabled={diyAddOnsDisabled}
              onChange={(next) => {
                setExclusivity(next)
                trackInteract('exclusivity_toggle')
              }}
            />
            <Toggle
              label="Include internal producer hours (1.8–2.4×)"
              checked={includeInternalOverhead}
              disabled={internalOverheadDisabled}
              onChange={(next) => {
                setIncludeInternalOverhead(next)
                trackInteract('overhead_toggle')
              }}
            />
          </div>
          {diyAddOnsDisabled && (
            <p className="text-xs text-slate-500">
              Whitelisting, raw footage, and exclusivity are treated as included in agency packages.
              Switch to DIY or Hybrid to model those line items explicitly.
            </p>
          )}

          <label className="block space-y-2">
            <span className="text-sm font-medium text-slate-800 dark:text-slate-100">
              Expected win rate ({expectedWinRatePct}%)
            </span>
            <input
              type="range"
              min={0}
              max={50}
              value={expectedWinRatePct}
              onChange={(e) => {
                setExpectedWinRatePct(clampWinRate(Number(e.target.value)))
                trackInteract('win_rate_change')
              }}
              className="w-full accent-teal-600"
            />
            <p className="text-xs text-slate-500">
              Share of monthly videos you expect to become scalable winners. Set to 0% to model an
              all-miss slate (cost-per-winner → ∞). Hybrid rebuilds floor at 0.25 videos only when
              win rate is above zero.
            </p>
          </label>

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={exportCsv}
              className="inline-flex items-center gap-2 rounded-xl bg-teal-700 px-4 py-2 text-sm font-medium text-white hover:bg-teal-800"
            >
              <ArrowDownTrayIcon className="h-4 w-4" aria-hidden="true" />
              Export CSV
            </button>
            <button
              type="button"
              onClick={reset}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <ArrowPathIcon className="h-4 w-4" aria-hidden="true" />
              Reset
            </button>
          </div>
        </section>

        <section className="space-y-4">
          <div className="rounded-2xl border border-teal-200 bg-gradient-to-br from-teal-50 to-slate-50 p-6 dark:border-teal-900 dark:from-teal-950/40 dark:to-slate-900">
            <p className="text-xs font-semibold uppercase tracking-wide text-teal-800 dark:text-teal-200">
              {PRODUCTION_MODES[productionMode].label} / month
            </p>
            <p className="mt-2 text-3xl font-semibold tabular-nums text-slate-900 dark:text-white">
              {formatUsdRange(results.selectedMonthly)}
            </p>
            <dl className="mt-4 grid gap-3 sm:grid-cols-2">
              <Stat label="Cost per creative" value={formatUsdRange(results.costPerCreative)} />
              <Stat
                label="Cost per winning video"
                value={formatUsdRange(results.costPerWinner)}
              />
              <Stat
                label="Expected winning videos"
                value={results.expectedWinners.toFixed(2)}
              />
              <Stat
                label={
                  productionMode === 'diy_creators'
                    ? 'Bundle discount'
                    : 'Bundle discount (DIY only)'
                }
                value={
                  productionMode === 'diy_creators'
                    ? results.bundleDiscountPct > 0
                      ? `${results.bundleDiscountPct}%`
                      : 'None'
                    : 'n/a'
                }
              />
            </dl>
            {isHybrid &&
              results.hybridRebuildCount !== results.expectedWinners &&
              results.expectedWinners > 0 && (
                <p className="mt-3 text-xs text-slate-600 dark:text-slate-300">
                  Hybrid rebuilds {results.hybridRebuildCount.toFixed(2)} videos (0.25 floor) while
                  cost-per-winner still uses {results.expectedWinners.toFixed(2)} expected winners.
                </p>
              )}
          </div>

          {(results.belowHealthyCadence ||
            (results.agencyWrongBuy && productionMode === 'agency') ||
            (results.agencyAboveTypicalRetainer && productionMode === 'agency')) && (
            <div className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
              <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
              <div className="space-y-1">
                {results.belowHealthyCadence && (
                  <p>
                    At {creativesPerMonth} finished videos/month you are below the typical{' '}
                    {HEALTHY_MONTHLY_CONCEPTS_MIN}–{HEALTHY_MONTHLY_CONCEPTS_MAX} video testing
                    slate. The cheapest win is usually more volume, not cheaper videos.
                  </p>
                )}
                {results.agencyWrongBuy && productionMode === 'agency' && (
                  <p>
                    Under about 4 videos/month, a studio retainer is usually the wrong buy versus
                    briefing freelancers directly.
                  </p>
                )}
                {results.agencyAboveTypicalRetainer && productionMode === 'agency' && (
                  <p>
                    At this volume the agency band exceeds the typical ~
                    {formatUsdRange({
                      low: AGENCY_TYPICAL_RETAINER_HIGH,
                      high: AGENCY_TYPICAL_RETAINER_HIGH,
                    })}
                    /mo retainer ceiling. Expect a custom package quote — the displayed high is
                    deliverable math, not a flat retainer.
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-900">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
              Compare all three paths
            </h3>
            <ul className="mt-3 space-y-2 text-sm">
              <CompareRow
                label="DIY creators (external)"
                value={formatUsdRange(results.diyExternalMonthly)}
                active={productionMode === 'diy_creators' && !includeInternalOverhead}
              />
              <CompareRow
                label="DIY all-in (with producer hours)"
                value={formatUsdRange(results.diyAllInMonthly)}
                active={productionMode === 'diy_creators' && includeInternalOverhead}
              />
              <CompareRow
                label="Agency / studio"
                value={formatUsdRange(results.agencyMonthly)}
                active={productionMode === 'agency'}
              />
              <CompareRow
                label="Hybrid AI → creators"
                value={formatUsdRange(results.hybridMonthly)}
                active={productionMode === 'hybrid_ai'}
              />
            </ul>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 text-sm dark:border-slate-700 dark:bg-slate-900">
            <h3 className="flex items-center gap-2 font-semibold text-slate-900 dark:text-white">
              <InformationCircleIcon className="h-4 w-4" aria-hidden="true" />
              {results.stackMode === 'hybrid_rebuild'
                ? 'Hybrid rebuild stack (mid-tier)'
                : 'Per-video stack'}
            </h3>
            <ul className="mt-3 space-y-1.5 text-slate-600 dark:text-slate-300">
              <li>Base: {formatUsdRange(results.basePerVideo)}</li>
              <li>Rights uplift: {formatUsdRange(results.rightsUpliftPerVideo)}</li>
              <li>Whitelist: {formatUsdRange(results.whitelistUpliftPerVideo)}</li>
              <li>Raw footage: {formatUsdRange(results.rawFootageUpliftPerVideo)}</li>
              <li>Exclusivity: {formatUsdRange(results.exclusivityUpliftPerVideo)}</li>
              <li>Revisions buffer (monthly): {formatUsdRange(results.revisionsBufferMonthly)}</li>
            </ul>
            {results.stackMode === 'agency_comparison' && (
              <p className="mt-3 text-xs text-slate-500">
                Stack shown for DIY comparison — the selected agency band is all-in and does not
                add these line items again.
              </p>
            )}
            {results.stackMode === 'hybrid_rebuild' && (
              <p className="mt-3 text-xs text-slate-500">
                Rates above match the mid-tier rebuild priced into the hybrid total (not the
                disabled creator-tier control). AI discovery scales with volume (~30 variants per
                unit) on top of this rebuild stack.
              </p>
            )}
          </div>
        </section>
      </div>

      <section className="grid gap-6 md:grid-cols-2">
        <RelatedList
          title="Related concepts"
          items={RELATED_GLOSSARY}
          hrefFor={(slug) => `/resources/glossary/${slug}`}
        />
        <RelatedList
          title="Related calculators"
          items={RELATED_TOOLS}
          hrefFor={(slug) => `/resources/tools/calculators/${slug}`}
        />
      </section>

      {faqItems.length > 0 && (
        <FAQ
          items={faqItems}
          type="single"
          disableSchema={true}
          features={{ showTitle: false }}
          theme={{ size: 'xl' }}
        />
      )}
    </div>
  )
}

/**
 * Implements ARIA radiogroup keyboard behavior (Arrow / Home / End) with roving tabindex.
 *
 * @param {KeyboardEvent} event - Keydown from the radiogroup container.
 * @param {ReadonlyArray<T>} options - Ordered option values.
 * @param {T} current - Currently selected value.
 * @param {(next: T) => void} onSelect - Selection callback.
 * @template T
 * @returns {void}
 */
function handleRadioGroupKeyDown<T extends string>(
  event: KeyboardEvent<HTMLDivElement>,
  options: ReadonlyArray<T>,
  current: T,
  onSelect: (next: T) => void,
): void {
  if (options.length === 0) return
  const idx = options.indexOf(current)
  const safeIdx = idx >= 0 ? idx : 0
  let nextIdx: number | null = null
  switch (event.key) {
    case 'ArrowRight':
    case 'ArrowDown':
      nextIdx = (safeIdx + 1) % options.length
      break
    case 'ArrowLeft':
    case 'ArrowUp':
      nextIdx = (safeIdx - 1 + options.length) % options.length
      break
    case 'Home':
      nextIdx = 0
      break
    case 'End':
      nextIdx = options.length - 1
      break
    default:
      return
  }
  event.preventDefault()
  const next = options[nextIdx]
  if (next !== undefined) {
    onSelect(next)
    const buttons = event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="radio"]')
    buttons[nextIdx]?.focus()
  }
}

function Toggle({
  label,
  checked,
  onChange,
  disabled = false,
}: {
  label: string
  checked: boolean
  onChange: (next: boolean) => void
  disabled?: boolean
}): JSX.Element {
  return (
    <label
      className={cn(
        'flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm dark:border-slate-700',
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="accent-teal-600"
      />
      <span>{label}</span>
    </label>
  )
}

function Stat({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-0.5 font-semibold tabular-nums text-slate-900 dark:text-white">{value}</dd>
    </div>
  )
}

function CompareRow({
  label,
  value,
  active,
}: {
  label: string
  value: string
  active: boolean
}): JSX.Element {
  return (
    <li
      className={cn(
        'flex items-center justify-between rounded-lg px-2 py-1.5',
        active && 'bg-teal-50 font-medium dark:bg-teal-950/40',
      )}
    >
      <span>{label}</span>
      <span className="tabular-nums">{value}</span>
    </li>
  )
}

function RelatedList({
  title,
  items,
  hrefFor,
}: {
  title: string
  items: ReadonlyArray<{ slug: string; label: string; note: string }>
  hrefFor: (slug: string) => string
}): JSX.Element {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-900">
      <h3 className="font-semibold text-slate-900 dark:text-white">{title}</h3>
      <ul className="mt-3 space-y-3 text-sm">
        {items.map((item) => (
          <li key={item.slug}>
            <Link
              href={hrefFor(item.slug)}
              className="font-medium text-teal-800 hover:underline dark:text-teal-300"
            >
              {item.label}
            </Link>
            <p className="text-slate-600 dark:text-slate-400">{item.note}</p>
          </li>
        ))}
      </ul>
    </div>
  )
}
